// Link giới thiệu của Shop (docs/KE_HOACH_SHOP_GIOI_THIEU.md): người đăng ký qua link thành Shop ngang hàng — cùng
// cấp trên, cùng bảng giá. Shop giới thiệu chỉ có link / mã QR và con số "đã giới thiệu N người"; không thấy
// danh sách hay tên shop được giới thiệu. CTV ở giao diện Bán hàng có link shop tự do (§9, `kind: 'free'`): shop
// đăng ký trực thuộc NPP, bảng khách vãng lai. Link cấp dưới của Đại lý / CTV vẫn ở trang Hệ thống.
//   · ShopReferralCard   — thẻ ở Cài đặt › Thông tin chung (chỉ Shop)
//   · ShopReferralDialog — mã QR to để người khác quét ngay (biểu tượng QR ở thanh trên): điện thoại trượt từ đáy,
//                          máy tính hộp giữa màn hình. CTV (`kind="free"`): chỉ QR, link và nút Chia sẻ.

import { useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Check, Copy, Download, Share2, X } from 'lucide-react'
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react'
import { toast } from 'sonner'
import { get } from '@/lib/api'
import { useIsMobile } from '@/hooks/useIsMobile'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface ShopReferral {
  code: string
  /** 'peer' — Shop (ngang hàng); 'free' — CTV (shop tự do). API cũ không trả → như 'peer' */
  kind?: 'peer' | 'free'
  enabled: boolean
  referredCount: number
}

/** Link giới thiệu + Sao chép / Chia sẻ / Tải QR. `canvasWrap` bọc QRCodeCanvas bản lớn (ẩn) cho nút Tải mã QR. */
function useShopReferral(enabled = true) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['shop-referral'],
    queryFn: () => get<ShopReferral>('/settings/shop/referral'),
    enabled,
  })
  const [copied, setCopied] = useState(false)
  const canvasWrap = useRef<HTMLDivElement>(null)
  // Link dựng từ mã + địa chỉ trang đang mở (như link của Đại lý / CTV)
  const link = data?.enabled ? `${window.location.origin}/signup?ref=${encodeURIComponent(data.code)}` : null
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function'

  function copy() {
    if (!link) return
    navigator.clipboard.writeText(link).then(
      () => {
        setCopied(true)
        toast.success('Đã sao chép link giới thiệu')
        setTimeout(() => setCopied(false), 2000)
      },
      () => toast.error('Trình duyệt chặn sao chép'),
    )
  }

  function share() {
    if (!link) return
    navigator.share({ title: 'ShopTrack', url: link }).catch(() => {
      /* người dùng đóng hộp chia sẻ */
    })
  }

  function downloadQr() {
    const canvas = canvasWrap.current?.querySelector('canvas')
    if (!canvas || !data) return
    const a = document.createElement('a')
    a.href = canvas.toDataURL('image/png')
    a.download = `shoptrack-gioi-thieu-${data.code}.png`
    a.click()
  }

  return { data, isLoading, isError, link, copied, canShare, copy, share, downloadQr, canvasWrap }
}

export function ShopReferralCard() {
  const { data, isLoading, link, copied, canShare, copy, share, downloadQr, canvasWrap } = useShopReferral()

  return (
    <section className="rounded-xl p-6 bg-raise space-y-4 shadow-card">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h3 className="font-medium text-base text-ink">Giới thiệu ShopTrack</h3>
        {data && (
          <p className="text-sm text-inkSoft">
            Bạn đã giới thiệu <span className="font-semibold tabular-nums text-ink">{data.referredCount}</span> người
          </p>
        )}
      </div>

      {isLoading ? (
        <p className="text-sm text-inkSoft">Đang tải…</p>
      ) : !data ? null : link ? (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
          <div className="self-start shrink-0 rounded-lg border border-rule bg-white p-2">
            <QRCodeSVG value={link} size={132} level="M" />
          </div>
          {/* Bản lớn cho nút "Tải mã QR" — không hiện */}
          <div ref={canvasWrap} className="hidden" aria-hidden>
            <QRCodeCanvas value={link} size={640} level="M" marginSize={4} />
          </div>
          <div className="min-w-0 flex-1 space-y-3">
            <div className="flex items-center gap-2 rounded-md border border-rule bg-paperSoft p-1.5 pl-2.5">
              <span className="min-w-0 flex-1 truncate select-all font-mono text-xs text-ink" title={link}>
                {link}
              </span>
              <Button type="button" size="sm" variant="outline" className="shrink-0" onClick={copy} aria-label="Sao chép link giới thiệu">
                {copied ? <Check className="text-ok" /> : <Copy />}
                <span className="max-sm:hidden">{copied ? 'Đã chép' : 'Sao chép'}</span>
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {canShare && (
                <Button type="button" size="sm" onClick={share}>
                  <Share2 /> Chia sẻ
                </Button>
              )}
              <Button type="button" size="sm" variant="outline" onClick={downloadQr}>
                <Download /> Tải mã QR
              </Button>
            </div>
            <p className="text-xs text-muted">
              Mã giới thiệu: <span className="font-mono text-ink">{data.code}</span>
            </p>
          </div>
        </div>
      ) : (
        <p className="text-sm text-inkSoft">Link giới thiệu tạm thời không dùng được.</p>
      )}
    </section>
  )
}

/**
 * Mã QR to + link, mở từ nút Giới thiệu ở thanh trên điện thoại: người bên cạnh quét là tới trang đăng ký. Chỉ tải
 * link khi mở (cùng khoá cache với thẻ ở Cài đặt).
 */
export function ShopReferralDialog({
  open,
  onOpenChange,
  title = 'Giới thiệu ShopTrack',
  kind = 'peer',
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: string
  /** 'free' — CTV: chỉ QR to, link, nút Chia sẻ (máy không chia sẻ được thì chép link) */
  kind?: 'peer' | 'free'
}) {
  const { data, isLoading, isError, link, copied, canShare, copy, share, downloadQr, canvasWrap } = useShopReferral(open)
  const phone = useIsMobile()

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            phone ? 'gx-scrim' : 'fixed inset-0 z-50 bg-black/40 backdrop-blur-sm',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed z-50 overflow-y-auto bg-raise shadow-overlay data-[state=closed]:animate-out data-[state=open]:animate-in',
            phone
              ? 'inset-x-0 bottom-0 max-h-[92dvh] rounded-t-3xl px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-3 data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom'
              : 'left-1/2 top-1/2 max-h-[90dvh] w-[min(92vw,420px)] -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-rule px-6 pb-6 pt-4 data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
          )}
        >
          {phone && <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-rule" aria-hidden />}
          <div className="flex items-center justify-between gap-3">
            <DialogPrimitive.Title className="text-lg font-semibold text-ink">{title}</DialogPrimitive.Title>
            <DialogPrimitive.Close className="-mr-2 grid size-11 place-items-center rounded-full text-inkSoft hover:bg-paperSoft active:bg-paperSoft" aria-label="Đóng">
              <X className="size-5" aria-hidden />
            </DialogPrimitive.Close>
          </div>
          <DialogPrimitive.Description className="sr-only">Mã QR và link đăng ký tài khoản Shop</DialogPrimitive.Description>

          {isLoading ? (
            <div className="mx-auto my-6 aspect-square w-[min(78vw,332px)] animate-pulse rounded-2xl bg-paperSoft" />
          ) : isError || !data ? (
            <p className="py-10 text-center text-sm text-inkSoft">Không tải được link giới thiệu</p>
          ) : link && kind === 'free' ? (
            <div className="flex flex-col items-center gap-4 pt-3">
              <div className="rounded-2xl bg-white p-4 shadow-card">
                <QRCodeSVG value={link} size={300} level="M" className="h-auto w-[min(70vw,300px)]" />
              </div>
              <p className="w-full select-all break-all rounded-xl border border-rule bg-paperSoft px-3 py-2.5 text-center font-mono text-xs text-ink">
                {link}
              </p>
              <Button type="button" className="w-full" onClick={canShare ? share : copy}>
                <Share2 /> Chia sẻ
              </Button>
            </div>
          ) : link ? (
            <div className="flex flex-col items-center gap-4 pt-3">
              {/* Nền trắng + lề trắng để máy quét bắt được mã */}
              <div className="rounded-2xl bg-white p-4 shadow-card">
                <QRCodeSVG value={link} size={300} level="M" className="h-auto w-[min(70vw,300px)]" />
              </div>
              <div ref={canvasWrap} className="hidden" aria-hidden>
                <QRCodeCanvas value={link} size={640} level="M" marginSize={4} />
              </div>
              <p className="text-sm text-inkSoft">
                Bạn đã giới thiệu <span className="font-semibold tabular-nums text-ink">{data.referredCount}</span>{' '}
                {data.kind === 'free' ? 'shop' : 'người'}
              </p>
              <div className="flex w-full items-center gap-2 rounded-xl border border-rule bg-paperSoft p-1.5 pl-3">
                <span className="min-w-0 flex-1 truncate select-all font-mono text-xs text-ink" title={link}>
                  {link}
                </span>
                <Button type="button" size="sm" variant="outline" className="shrink-0" onClick={copy}>
                  {copied ? <Check className="text-ok" /> : <Copy />}
                  {copied ? 'Đã chép' : 'Sao chép'}
                </Button>
              </div>
              <div className="grid w-full grid-cols-2 gap-2">
                {canShare && (
                  <Button type="button" onClick={share}>
                    <Share2 /> Chia sẻ
                  </Button>
                )}
                <Button type="button" variant="outline" onClick={downloadQr} className={canShare ? undefined : 'col-span-2'}>
                  <Download /> Tải mã QR
                </Button>
              </div>
            </div>
          ) : (
            <p className="py-10 text-center text-sm text-inkSoft">Link giới thiệu tạm thời không dùng được.</p>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
